// ✅ src/pages/VehicleV2/VehicelAllIssuedetailsprint.jsx  (REACT PRINT PAGE)
// React replacement for: VehicelAllIssuedetailsprint.aspx (Crystal print)
//
// ✅ Reads params from URL:
//   /vehicle/VehicelAllIssuedetailsprint?vehicleId=79&storeId=3&fromDate=01/04/2015&todate=20/01/2016
//   OR (recommended from our listing page):
//   /vehicle/VehicelAllIssuedetailsprint?vehicleId=79&storeId=3&fromDate=01/04/2015&toDate=20/01/2016
//   OR ISO:
//   fromDate=2026-02-01&toDate=2026-02-21
//
// ✅ Calls backend:
//   GET /api/vehicleAllIssueDetails/meta?branchId=8&branchType=HO&loginUserId=37  (for header names)
//   GET /api/vehicleAllIssueDetails/list?branchId=8&branchType=HO&loginUserId=37
//      &vehicleId=79&storeId=3&fromDate=YYYY-MM-DD&toDate=YYYY-MM-DD&page=1&pageSize=2000
//
// ✅ Prints with NISSI header template (like your sample)
// ✅ Auto window.print()

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/AllIssueDetailsRpt";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const isISO = (s) => /^\d{4}-\d{2}-\d{2}$/.test(toStr(s));
const isDDMM = (s) => /^\d{2}\/\d{2}\/\d{4}$/.test(toStr(s));

const ddmmyyyyToISO = (s) => {
  const t = toStr(s);
  if (!t) return "";
  const [dd, mm, yyyy] = t.split("/");
  if (!dd || !mm || !yyyy) return "";
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
};

const isoToDDMMYYYY = (s) => {
  const t = toStr(s);
  if (!t) return "";
  const [yyyy, mm, dd] = t.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
};

const normalizeDateToISO = (any) => {
  const s = toStr(any);
  if (!s) return "";
  if (isISO(s)) return s;
  if (isDDMM(s)) return ddmmyyyyToISO(s);
  return ""; // unknown format
};

const fmtMoney = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const pick = (row, keys, def = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return def;
};

export default function VehicelAllIssuedetailsprint() {
  const [sp] = useSearchParams();

  // URL params
  const vehicleId = toStr(sp.get("vehicleId") || sp.get("vehicleid"));
  const storeId = toStr(sp.get("storeId") || sp.get("storeid"));

  // support both dd/MM/yyyy & ISO & also misspelling "todate"
  const fromDateISO = normalizeDateToISO(sp.get("fromDate") || sp.get("fromdate"));
  const toDateISO = normalizeDateToISO(sp.get("toDate") || sp.get("todate"));

  // session equivalents
  const branchId = toInt(localStorage.getItem("branchid") || 8, 8);
  const loginUserId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType = localStorage.getItem("branchtype") || "HO";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [meta, setMeta] = useState({ vehicles: [], stores: [] });
  const [rows, setRows] = useState([]);

  const load = async () => {
    setLoading(true);
    setErr("");

    try {
      if (!vehicleId) throw new Error("Missing vehicleId in URL");
      if (!storeId) throw new Error("Missing storeId in URL");

      // meta for labels
      const metaRs = await axios.get(`${API_BASE}/meta`, {
        params: { branchId, branchType, loginUserId },
      });
      if (!metaRs.data?.success) throw new Error(metaRs.data?.message || "Meta load failed");
      setMeta({
        vehicles: metaRs.data.vehicles || [],
        stores: metaRs.data.stores || [],
      });

      // list for print (big pageSize)
      const listRs = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId,
          branchType,
          loginUserId,
          vehicleId,
          storeId,
          fromDate: fromDateISO,
          toDate: toDateISO,
          page: 1,
          pageSize: 2000,
        },
      });
      if (!listRs.data?.success) throw new Error(listRs.data?.message || "List load failed");

      setRows(listRs.data.rows || []);

      // auto print
      setTimeout(() => window.print(), 400);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const header = useMemo(() => {
    const vehicleLabel =
      meta.vehicles.find((v) => toStr(v.value) === toStr(vehicleId))?.label || "";
    const storeLabel =
      meta.stores.find((s) => toStr(s.value) === toStr(storeId))?.label || "";

    return {
      reportTitle: "Vehicle Issue Details Report",
      vehicleLabel,
      storeLabel,
      fromDateText: fromDateISO ? isoToDDMMYYYY(fromDateISO) : "",
      toDateText: toDateISO ? isoToDDMMYYYY(toDateISO) : "",
    };
  }, [meta, vehicleId, storeId, fromDateISO, toDateISO]);

  if (loading) {
    return (
      <div style={{ padding: 20, fontFamily: "Arial, sans-serif" }}>
        Loading print...
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ padding: 20, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Print failed</div>
        <div style={{ color: "crimson" }}>{err}</div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", fontFamily: "Arial, sans-serif" }}>
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display:none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #000; padding: 6px; font-size: 11px; }
        th { font-weight: 700; text-align: center; }
      `}</style>

      {/* Outer Border */}
      <div style={{ border: "2px solid #000", padding: 10 }}>
        {/* Header block */}
        <div style={{ border: "1px solid #000", padding: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Logo block (same as your sample) */}
            <div
              style={{
                width: 90,
                height: 70,
                border: "1px solid #000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#1f2937",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              <div style={{ textAlign: "center", fontSize: 10, lineHeight: 1.2 }}>
                <div>NISSI</div>
                <div>INFOTECH</div>
                <div>PVT LTD</div>
              </div>
            </div>

            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>Nissi Infotech</div>
              <div style={{ fontWeight: 700, fontSize: 12, marginTop: 2 }}>Nissi</div>
              <div style={{ fontWeight: 700, fontSize: 12 }}>CIVIL CONSTRUCTION</div>

              <div style={{ fontSize: 11, marginTop: 6, lineHeight: 1.3 }}>
                <div>
                  3/42-12, Nagappa Colony, kavundampalayam - 641030 . Tamil Nadu . India .
                </div>
                <div>Phone No : 9003744644</div>
                <div>infracnsoft-implementation@nissiinfotech.in</div>
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div style={{ borderLeft: "1px solid #000", borderRight: "1px solid #000", borderBottom: "1px solid #000" }}>
          <div style={{ textAlign: "center", fontWeight: 700, padding: "8px 0" }}>
            {header.reportTitle}
          </div>
        </div>

        {/* Top info */}
        <div style={{ border: "1px solid #000", borderTop: "0", padding: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, fontSize: 12 }}>
            <div style={{ lineHeight: 1.8 }}>
              <div>
                <b>Equipment</b> : {header.vehicleLabel || vehicleId || "-"}
              </div>
              <div>
                <b>Store</b> : {header.storeLabel || storeId || "-"}
              </div>
            </div>

            <div style={{ lineHeight: 1.8 }}>
              <div>
                <b>From Date</b> : {header.fromDateText || "-"}
              </div>
              <div>
                <b>To Date</b> : {header.toDateText || "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ border: "1px solid #000", borderTop: "0", padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 45 }}>S.No</th>
                <th>Vehicle Name</th>
                <th style={{ width: 90 }}>Vehicle Code</th>
                <th style={{ width: 110 }}>Vehicle Reg No</th>
                <th style={{ width: 90 }}>Date</th>
                <th>Item Name</th>
                <th style={{ width: 55 }}>Qty</th>
                <th style={{ width: 75 }}>Price</th>
                <th style={{ width: 90 }}>Serial No</th>
                <th style={{ width: 95 }}>Battery Type</th>
                <th style={{ width: 95 }}>Battery Make</th>
                <th style={{ width: 110 }}>Supplier</th>
                <th style={{ width: 90 }}>Tyre Make</th>
                <th style={{ width: 90 }}>Tyre Model</th>
                <th style={{ width: 85 }}>Amount</th>
                <th style={{ width: 110 }}>Store</th>
              </tr>
            </thead>

            <tbody>
              {(rows || []).map((r, idx) => {
                const vehName = pick(r, ["vehiclename", "VehicleName", "fld_vehicle_name"]);
                const vehCode = pick(r, ["vehiclecode", "VehicleCode", "fld_vehicle_code"]);
                const regNo = pick(r, ["vehicleregno", "VehicleRegNo", "fld_vehicle_regno"]);
                const date = pick(r, ["fld_date", "date", "issuedate"]);
                const item = pick(r, ["fld_itemname", "itemname", "ItemName"]);
                const qty = pick(r, ["fld_qty", "qty", "Qty"], "0");
                const price = pick(r, ["fld_price", "price", "Price"], "0");
                const serialNo = pick(r, ["fld_serialno", "serialno", "SerialNo"]);
                const batteryType = pick(r, ["fld_batterytype", "batterytype", "BatteryType"]);
                const batteryMake = pick(r, ["fld_batterymake", "batterymake", "BatteryMake"]);
                const supplier = pick(r, ["fld_suppliername", "suppliername", "Supplier"]);
                const tyreMake = pick(r, ["fld_tyremake", "tyremake", "TyreMake"]);
                const tyreModel = pick(r, ["fld_tyre_model", "tyremodel", "TyreModel"]);
                const amount = pick(r, ["fld_amount", "amount", "Amount"], "0");
                const storeName = pick(r, ["fld_storename", "storename", "StoreName"]);

                return (
                  <tr key={idx}>
                    <td style={{ textAlign: "center" }}>{idx + 1}</td>
                    <td>{vehName || "-"}</td>
                    <td>{vehCode || "-"}</td>
                    <td>{regNo || "-"}</td>
                    <td style={{ textAlign: "center" }}>{date || "-"}</td>
                    <td>{item || "-"}</td>
                    <td style={{ textAlign: "right" }}>{qty}</td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(price)}</td>
                    <td>{serialNo || "-"}</td>
                    <td>{batteryType || "-"}</td>
                    <td>{batteryMake || "-"}</td>
                    <td>{supplier || "-"}</td>
                    <td>{tyreMake || "-"}</td>
                    <td>{tyreModel || "-"}</td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(amount)}</td>
                    <td>{storeName || header.storeLabel || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Blank space area like your sample */}
          <div style={{ height: 160, borderTop: "1px solid #000" }} />
        </div>

        {/* Footer */}
        <div style={{ border: "1px solid #000", borderTop: "0", padding: 10, fontSize: 11 }}>
          Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
        </div>

        {/* Print button (hidden in print) */}
        <div className="no-print" style={{ marginTop: 12, textAlign: "center" }}>
          <button
            onClick={() => window.print()}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "1px solid #333",
              cursor: "pointer",
              background: "#1d4ed8",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            Print
          </button>
        </div>
      </div>
    </div>
  );
}